import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import Button from '../components/common/Button';
import '../styles/contact-page.css';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_o5xa3wr';
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_uybsa78';
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'OPDnwSWUGHjTYHdD9';

type SubmitStatus = 'idle' | 'sending' | 'success' | 'error';

const CheckIcon = () => (
  <svg className="contact-checklist__check" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: name,
          from_email: email,
          company,
          phone,
          message,
        },
        PUBLIC_KEY,
      );
      setStatus('success');
      setName('');
      setEmail('');
      setCompany('');
      setPhone('');
      setMessage('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero__content fade-in-up">
            <h1 className="contact-hero__headline">
              Let's Talk Security&nbsp;Reporting
            </h1>
            <p className="contact-hero__sub">
              Get in touch with our team for a custom demonstration and see how
              Inferium Reports can streamline your operations.
            </p>
            <div className="contact-hero__buttons fade-in-up-delay">
              <Button variant="primary" size="lg" href="#contact-form">
                Get Started Today
              </Button>
              <Button variant="outline" size="lg" href="https://login.inferiumreports.com" target="_blank">
                Client Login
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Info Grid */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-main__grid fade-in-up">
            {/* Form Card */}
            <div className="contact-form-card" id="contact-form">
              <h2 className="contact-form-card__title">Request a Consultation</h2>
              <p className="contact-form-card__subtitle">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {status === 'success' && (
                <div className="contact-alert contact-alert--success">
                  Thank you! Your message has been sent successfully. We'll get back to you shortly.
                </div>
              )}

              {status === 'error' && (
                <div className="contact-alert contact-alert--error">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="contact-form__row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-input"
                      placeholder="john@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="contact-form__row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="company">Company *</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="form-input"
                      placeholder="Your company name"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-input"
                      placeholder="(555) 000-0000"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-textarea"
                    placeholder="Tell us about your security reporting needs..."
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  type="submit"
                  style={{ width: '100%' }}
                  loading={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Info Sidebar */}
            <div className="contact-sidebar">
              {/* What you get */}
              <div className="contact-info-card">
                <div className="contact-info-card__icon contact-info-card__icon--green">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="22,4 12,14.01 9,11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="contact-info-card__title">What's Included</h3>
                <p className="contact-info-card__desc">
                  Every implementation comes with everything you need to succeed.
                </p>
                <ul className="contact-checklist">
                  <li><CheckIcon /> Custom platform setup and configuration</li>
                  <li><CheckIcon /> Comprehensive user training and onboarding</li>
                  <li><CheckIcon /> Ongoing technical support and maintenance</li>
                  <li><CheckIcon /> Integration with existing security systems</li>
                </ul>
              </div>

              {/* Client login */}
              <div className="contact-info-card">
                <div className="contact-info-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="contact-info-card__title">Existing Client?</h3>
                <p className="contact-info-card__desc">
                  Access your dashboard to manage reports, view analytics, and monitor
                  your security operations.
                </p>
                <div style={{ marginTop: '1.25rem' }}>
                  <Button variant="outline" size="md" href="https://login.inferiumreports.com" target="_blank">
                    Client Login Portal
                  </Button>
                </div>
              </div>

              {/* FAQ */}
              <div className="contact-info-card">
                <div className="contact-info-card__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="contact-info-card__title">Common Questions</h3>
                <div className="contact-faq">
                  <div className="contact-faq__item">
                    <h4>How quickly can we get started?</h4>
                    <p>
                      Inferium Reports can be set up within an hour and has a very
                      intuitive guard interface.
                    </p>
                  </div>
                  <div className="contact-faq__item">
                    <h4>Is training included?</h4>
                    <p>
                      Yes, we have a comprehensive video library for onboarding and can
                      offer one-on-one demos to aid in setup.
                    </p>
                  </div>
                  <div className="contact-faq__item">
                    <h4>What devices are supported?</h4>
                    <p>
                      Inferium Reports has full support on a mobile app for iOS and
                      Android with a web app interface for administrative features.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="contact-cta">
        <div className="container">
          <div className="contact-cta__inner fade-in-up">
            <h2 className="contact-cta__heading">Ready to Get Started?</h2>
            <p className="contact-cta__sub">
              Join organizations who trust Inferium Reports for their
              security reporting and monitoring needs.
            </p>
            <div className="contact-cta__buttons">
              <Button variant="primary" size="lg" href="#contact-form">
                Schedule Consultation
              </Button>
              <Button variant="secondary" size="lg" href="/features">
                View All Features
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
